import { FormattedMessage, defineMessages } from "react-intl";
import FormGroup from "../FormGroup";
import MarkdownEditor from "../MarkdownEditor";
import NumberInput from "../NumberInput";
import {
  BaseProjectVersionApprovalButton,
  FragmentKey,
  Messages,
} from "./Base";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/kit/ui/tooltip";
import { HelpCircleIcon } from "@/icons";
import { Controller } from "react-hook-form";
import SuggestionPlugin from "../MarkdownEditor/plugins/Suggestion";

const messages = defineMessages<keyof Messages>({
  dialogTriggerCreate: { defaultMessage: "Approve" },
  dialogTriggerUpdate: { defaultMessage: "Approved" },
  dialogTitle: { defaultMessage: `Approve submission "{version}" of {entity}` },
  createSubmit: { defaultMessage: "Approve" },
  onCreateSuccess: {
    defaultMessage: "{entity}'s submission has been approved!",
  },
  onCreateFailure: {
    defaultMessage:
      "{entity}'s submission cannot be approved at the moment, please retry later.",
  },
  updateSubmit: { defaultMessage: "Update approval" },
  onUpdateSuccess: {
    defaultMessage: "{entity}'s submission approval has been updated!",
  },
  onUpdateFailure: {
    defaultMessage:
      "{entity}'s submission approval cannot be updated at the moment, please retry later.",
  },
  deleteSubmit: { defaultMessage: "Withdraw approval" },
  onDeleteSuccess: {
    defaultMessage:
      "You have just withdrawn your approval on {entity}'s submission",
  },
  onDeleteFailure: {
    defaultMessage:
      "{entity}'s submission approval cannot be withdrawn at the moment, please retry later.",
  },
});

interface FormData {
  scores: { value?: number; competitionScoringCriterionId: string }[];
  notes: string;
}

interface Props {
  projectVersion: FragmentKey;
}

const CriterionLabel = ({
  title,
  description,
}: {
  title: string;
  description?: string | null;
}) => {
  return (
    <span>
      {title}{" "}
      {description && (
        <Tooltip delayDuration={0}>
          <TooltipTrigger
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <HelpCircleIcon className="inline h-4 w-4 text-gray-500" />
          </TooltipTrigger>
          <TooltipContent>{description}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
};

export default function ProjectVersionApproveButton(props: Props) {
  return (
    <BaseProjectVersionApprovalButton<FormData>
      fragment={props.projectVersion}
      messages={messages}
      isHidden={({ approval }) => !!approval && !approval.accept}
      getDefaultValues={({
        approval,
        project: {
          competition: { scoringCriteria },
        },
      }) => ({
        scores:
          approval?.scores.map((score) => ({
            value: score.value,
            competitionScoringCriterionId: score.competitionScoringCriterion.id,
          })) ??
          scoringCriteria.map((criterion) => ({
            competitionScoringCriterionId: criterion.id,
          })),
        notes: approval?.notes ?? undefined,
      })}
      getCreateInput={(data) => ({
        accept: {
          scores: data.scores,
          notes: data.notes || undefined,
        },
      })}
      getUpdateInput={(data) => ({
        accept: {
          scores: data.scores,
          notes: data.notes || null,
        },
      })}
    >
      {(form, scoringCriteria) => {
        return (
          <>
            {scoringCriteria.map(({ id, title, description }, index) => (
              <FormGroup
                key={id}
                label={
                  <CriterionLabel title={title} description={description} />
                }
              >
                <input
                  type="hidden"
                  value={id}
                  {...form.register(
                    `scores.${index}.competitionScoringCriterionId`,
                  )}
                />
                <NumberInput
                  {...form.register(`scores.${index}.value`, {
                    valueAsNumber: true,
                  })}
                />
              </FormGroup>
            ))}

            <p className="gap-2 inline-flex flex-row items-center">
              <FormattedMessage defaultMessage="Notes" />
            </p>

            <Controller
              control={form.control}
              name="notes"
              rules={{ maxLength: 2000 }}
              render={({ field: { onChange, onBlur, value, name, ref } }) => (
                <>
                  <MarkdownEditor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  >
                    <SuggestionPlugin />
                  </MarkdownEditor>
                  {/*
                    Workaround to keep the browser’s native form-validation working.
                    Lexical renders content through its own pipeline, so it currently
                    bypasses the built-in validation mechanism
                  */}
                  <input
                    type="text"
                    className="sr-only"
                    aria-hidden="true"
                    tabIndex={-1}
                    name={name}
                    value={value ?? ""}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                  />
                </>
              )}
            />
          </>
        );
      }}
    </BaseProjectVersionApprovalButton>
  );
}
